import { VersionedData } from '@webapp/util/AsyncData'
import { DateTime, DateTimeUnit } from 'luxon'
import { Environment } from '@webapp/util/Environment'
import { CompositeId } from '@webapp/rd/CompositeId'
import { CountryCodes, GeoUtil } from '@webapp/util/GeoUtil'
import { osInfos, SyncState, ViewPerson, workspaceTypeInfos } from '@webapp/rd/ResearchDataModel'
import type { WorkspaceTypeInfo } from '@webapp/rd/ResearchDataModel'
import { getPossessive } from '@webapp/util/LanguageUtil'
import { ToolIcon } from '@zhuowenli/vue-feather-icons'
import { isDefined } from '@webapp/util/TypeScriptUtil'
import { isTestStringId } from '@webapp/util/TestId'
import { countBy, sortBy } from 'lodash'

export class User implements VersionedData {
    id?: string
    issuer?: string
    subject?: string
    givenName?: string
    surname?: string
    namePrivacy: PrivacyLevel = PrivacyLevel.Me
    gender?: UserGender
    genderPrivacy: PrivacyLevel = PrivacyLevel.Me
    photoUrl?: string
    photoPrivacy: PrivacyLevel = PrivacyLevel.Me
    email?: string
    emailPrivacy: PrivacyLevel = PrivacyLevel.Me
    email2?: string
    email2Privacy: PrivacyLevel = PrivacyLevel.Me
    mobileNumber?: string
    mobileNumberPrivacy: PrivacyLevel = PrivacyLevel.Me
    profileDataGroupId?: string
    metadata?: UserMetadata
    limits?: UserLimits
    limitsDisabled = false
    invited = false
    createdUserId?: string
    createdDate?: DateTime
    modifiedUserId?: string
    modifiedDate?: DateTime

    get isTestUser() {
        return isTestStringId(this.id)
    }

    get isSystemUser() {
        return this.issuer != Environment.get('IDENTITY_URL');
    }

    get displayName() {
        if (this.givenName && this.surname) {
            return this.givenName + ' ' + this.surname;
        }
        return (this.givenName ?? '') + (this.surname ?? '');
    }

    get pageUrl() { return User.getPageUrl(this.id) }

    get isSharingLimited() { return !this.limitsDisabled && !!this.limits?.sharingLimit }

    static getPageUrl(userId: string | undefined) {
        return `/users/${userId}`
    }
}

export class UserMetadata {
    nameConfirmed = false
    location?: string
    appSupportNotify?: string[]
    genealogyStartYear?: number
    researchTools?: string[]
    showResearchActivity = false
    showFamilySurnames = false
    socialAccounts?: string[]
    dismissed?: string[]

    get countryName() { return GeoUtil.getCountryName(this.location)}
    get countryCode() { return CountryCodes.fromName(this.countryName) }

    hasSocialAccount(providerId: string) {
        return this.socialAccounts?.some(a => a.startsWith(providerId + ':')) ?? false
    }

    getSocialUsernameFor(providerId: string) {
        const account = this.socialAccounts?.find(a => a.startsWith(providerId + ':'))
        return account ? account.split(':')[1] : undefined
    }

    getSocialAccountUrlFor(providerId: string) {
        const account = this.socialAccounts?.find(a => a.startsWith(providerId + ':'))
        const provider = socialAccountProviders.find(p => p.id == providerId)
        const username = account?.split(':', 2)[1]
        return username ? provider?.getAccountUrl(username) : undefined
    }
}

export class UserFoundDataFile {
    deviceType?: string
    filePathHash?: string
    fileType?: string
    isSupported = false
    modifiedDate?: DateTime
    personCount?: number
    foundDate?: DateTime
}

export interface SocialAccountProvider {
    id: string
    name: string
    icon: string
    getAccountUrl: (username: string) => string
}

export const socialAccountProviders: SocialAccountProvider[] = [ // in display order
    { 
        id: 'x', 
        name: 'X', 
        icon: new URL('@/assets/logos/X.png', import.meta.url).href,
        getAccountUrl: (username: string) => `https://x.com/${username}`,
    },
    { 
        id: 'facebook', 
        name: 'Facebook', 
        icon: new URL('@/assets/logos/Facebook.png', import.meta.url).href,
        getAccountUrl: (username: string) => `https://facebook.com/${username}`,
    },
]

export class UserLimits {
    researchGroupLimit?: number
    sharingLimit?: number
    shareAnyOwner = false
}

export class UserUsage {
    userId?: string
    totalResearchGroups?: number
    syncDeviceTypes?: string[]
    workspaceTypes?: string[]
    lastSyncDate?: DateTime
    lastSyncState?: SyncState
    enabledResearchGroups?: number
    sharingUsers?: number
    sharingInvites?: number
    sharingLimitReached = false
    totalFamilyGroups?: number
    clientActivity?: UserClientActivity[]

    get syncOSInfos() {
        return this.syncDeviceTypes?.map(t => osInfos.find(n => n.id == t)).filter(isDefined) ?? []
    }

    get workspaceBaseTypes() {
        const baseTypes = this.workspaceTypes?.map(t => t.split('.')[0])
        const baseTypeFreq = sortBy(Object.entries(countBy(baseTypes ?? [])), ([, count]) => -count)
        return (baseTypeFreq.map(([t, n]) => ({ type: workspaceTypeInfos.find(wt => wt.id == t), count: n }))
            .filter(wt => wt.type) ?? []) as { type: WorkspaceTypeInfo, count: number }[]
    }
}

export class UserClientActivity {
    userId?: string
    clientId?: string
    authorizedDate?: DateTime
    lastActivityDate?: DateTime
    activityDays?: string
}

export class UserSupervisor implements VersionedData {
    id?: string
    supervisorId?: string
    forUserId?: string
    createdDate?: DateTime
    createdUserId?: string
    modifiedDate?: DateTime // NOTE: this is not currently supported by the server (always undefined)
}

export enum UserSort {
    Surname = "Surname",
    GivenName = "GivenName",
    CreatedDate = "CreatedDate",
}

export class DataGroup implements VersionedData {
    id?: string
    groupType = DataGroupType.Profile
    name?: string
    description?: string
    startItemId?: string
    workspaceId?: string
    trackedGroupId?: string
    isChild = false
    isUserProfile = false
    createdUserId?: string
    createdDate?: DateTime
    modifiedUserId?: string
    modifiedDate?: DateTime
    ownerId?: string
    workspaceType?: string
    disabled = false

    get isProfile() { return this.groupType == DataGroupType.Profile}
    get pageUrl() { return this.isProfile ? DataGroup.getProfilePageUrl(this.startItemId) : DataGroup.getResearchPageUrl(this.id) }

    get startPersonPageUrl() { return this.startItemId ? ViewPerson.getPageUrl(this.startItemId) : undefined }

    static getProfilePageUrl(profilePersonId: string | undefined) {
        return `/profiles/${profilePersonId}`
    }

    static getResearchPageUrl(dataGroupId: string | undefined) {
        return `/trees/${dataGroupId}`
    }
}

export class DataGroupMember {
    id?: string
    dataGroupId?: string
    groupType = DataGroupType.Profile
    principalType = PrincipalType.User
    principalId?: string
    role = GroupMemberRole.Viewer
    disabled = false
    modifiedUserId?: string
    modifiedDate?: DateTime

    get key() { 
        return this.dataGroupId && this.id ? DataGroupMember.buildKey(this.dataGroupId, this.id) : undefined
    }

    static buildKey(dataGroupId: string, id: string) {
        return new CompositeId(dataGroupId, id).value
    }

    static parseKey(key: string) {
        const compID = CompositeId.parse(key)
        return { dataGroupId: compID.groupId, id: compID.itemId } as DataGroupMemberKey
    }

    static toKeyString(key: DataGroupMemberKey) {
        return DataGroupMember.buildKey(key.dataGroupId, key.id)
    }
}

export interface DataGroupMemberKey {
    id: string
    dataGroupId: string
}

export class DataGroupInvite {
    id?: string
    senderUserId?: string
    dataGroupId?: string
    principalType = PrincipalType.FamilyGroup
    principalId?: string
    role = GroupMemberRole.Viewer
    senderMessage?: string    
    lastSentDate?: DateTime    
    expirationDate?: DateTime    
    dataGroupApproval = ApprovalStatus.Pending
    principalApproval = ApprovalStatus.Pending
    closedDate?: DateTime    
    result = InvitationResult.Pending
    modifiedDate?: DateTime    
    modifiedUserId?: string

    get isExpired() { return !this.expirationDate || DateTime.utc() > this.expirationDate }

    get isPending() { return this.result == InvitationResult.Pending }

    get status() {
        return this.dataGroupApproval == ApprovalStatus.Rejected ? DataGroupInviteStatus.Rejected : // always show even if expired
            this.result == InvitationResult.Accepted ? DataGroupInviteStatus.Approved : // only true if invite did not expire
            this.isExpired ? DataGroupInviteStatus.Expired :
            DataGroupInviteStatus.Pending
    }

    get statusText() { return dataGroupInviteStatusText[this.status] }
}

export type TrustLevel = 0 | 1 | 2

export class ViewDataGroup {
    id?: string
    groupType =  DataGroupType.Profile
    startItemId?: string
    trackedGroupId?: string
    trustLevel: TrustLevel = 2 // least by default
    permissions = ItemPermissions.None
}

export const automatchGroupId = 'SYS000'

export class FamilyGroup implements VersionedData {
    id?: string
    name?: string
    description?: string
    scopeFamilyId?: string
    photoUrl?: string
    createdUserId?: string
    createdDate?: DateTime
    modifiedUserId?: string
    modifiedDate?: DateTime
    ownerId?: string

    get pageUrl() { return FamilyGroup.getPageUrl(this.id) }

    static getPageUrl(familyGroupId: string | undefined) {
        return `/groups/${familyGroupId}`
    }

    static compareName(a: FamilyGroup, b: FamilyGroup) {
        return (a.name ?? '').localeCompare(b.name ?? '')
    }

    static getDefaultName(owner: User) {
        return `${getPossessive(owner.displayName, owner.gender)} group`
    }
}

export class FamilyGroupMember implements VersionedData {
    id?: string
    familyGroupId?: string
    userId?: string
    role = GroupMemberRole.Member
    visibility = GroupMemberVisibility.Limited
    createdDate?: DateTime
    modifiedUserId?: string
    modifiedDate?: DateTime

    get key() { 
        return this.familyGroupId && this.id ? FamilyGroupMember.buildKey(this.familyGroupId, this.id) : undefined
    }

    get isOwner() { return this.role == GroupMemberRole.Owner }

    get canInviteOthers() { 
        return this.role == GroupMemberRole.Owner ||
            this.role == GroupMemberRole.Administrator ||
            this.role == GroupMemberRole.Recruiter
    }

    get userPageUrl() { return User.getPageUrl(this.userId) }

    static buildKey(familyGroupId: string, id: string) {
        return new CompositeId(familyGroupId, id).value
    }

    static parseKey(key: string) {
        const compID = CompositeId.parse(key)
        return { familyGroupId: compID.groupId, id: compID.itemId } as FamilyGroupMemberKey
    }

    static toKeyString(key: FamilyGroupMemberKey) {
        return FamilyGroupMember.buildKey(key.familyGroupId, key.id)
    }
   
}

export interface FamilyGroupMemberKey {
    id: string
    familyGroupId: string
}

export class FamilyGroupInvite implements VersionedData {
    id?: string
    senderUserId?: string
    familyGroupId?: string
    profileDataGroupId?: string
    profileDisplayName?: string
    profileGender?: UserGender
    recipientEmail?: string
    recipientMobileNumber?: string
    recipientUserId?: string
    recipientRole = GroupMemberRole.Member
    recipientFirstName?: string
    recipientLastName?: string
    senderMessage?: string
    activationCode?: string
    encourageToInvite?: boolean
    lastSentDate?: DateTime
    expirationDate?: DateTime
    closedDate?: DateTime
    result = InvitationResult.Pending
    modifiedDate?: DateTime
    modifiedUserId?: string
    senderUser?: User
    familyGroup?: FamilyGroup
    profileDataGroup?: DataGroup
    recipientUser?: User
    workspaceType?: string

    get recipientDisplayName() { 
        if (this.recipientFirstName && this.recipientLastName) {
            return `${this.recipientFirstName} ${this.recipientLastName}`
        }
        return this.recipientFirstName ?? this.recipientLastName ?? this.recipientEmail ?? this.recipientMobileNumber ?? '(unknown)'
    }

    get isPending() { return this.result == InvitationResult.Pending }
    
    getAcceptUrl(code?: string) { return `/groupinvites/${this.id}/accept?code=${encodeURIComponent(code ?? '')}` }
}

export class Subscription implements VersionedData {
    id?: string
    userId?: string
    providerSubscriptionId?: string
    providerCustomerId?: string
    startDate?: DateTime
    status = SubscriptionStatus.Cancelled
    paymentMethodId?: string
    currentPeriodStart?: DateTime
    currentPeriodEnd?: DateTime
    createdDate?: DateTime
    createdUserId?: string
    modifiedDate?: DateTime // NOTE: this is not currently supported by the server (always undefined)
    items: SubscriptionItem[] = []
}

export class SubscriptionItem {
    id?: string
    priceId?: string
    quantity = 0
    price?: ProductPrice // nested resource
}

export class ProductPrice {
    id?: string
    productId?: string
    currency = "USD"
    unitPrice = 0
    intervalUnit: DateTimeUnit = 'month'
    intervalCount = 1
}

export interface AddSubscriptionResponse {
    subscriptionId: string
    providerSubscriptionId: string
    paymentIntentId?: string
    clientSecret?: string
}

export class PaymentMethod {
    id?: string
    providerCustomerId?: string
    paymentType: PaymentType = PaymentType.Card
    brand?: string // e.g. "Visa ***1234"
    expirationDate?: DateTime
    lastDigits?: string
    billingName?: string
    modifiedDate?: DateTime

    get identifier() { return `${this.brand} (xxx${this.lastDigits})` }
}

export class Payment {
    id?: string
    subscriptionId?: string
    methodId?: string
    date?: DateTime
    amount = 0
    status = PaymentStatus.Pending
}

export class NewSupportTicket {
    firstName?: string
    lastName?: string
    email?: string
    inquiryType = SupportInquiryType.Question
    priority = SupportTicketPriority.Medium
    subject?: string
    message?: string
    captchaCode?: string
}

export class SupportTicket implements VersionedData {
    id?: string
    inquiryType = SupportInquiryType.Question
    priority = SupportTicketPriority.Medium
    subject?: string
    message?: string
    contactId?: string
    status = SupportTicketStatus.Open
    webUrl?: string
    createdDate?: DateTime
    modifiedDate?: DateTime
    createdToken?: string
}

export enum SupportInquiryType {
    Question = "Question",
    Request = "Request",
    Problem = "Problem",
    Other = "Other",
}

export const supportInquiryTypeInfos = [
  { value: SupportInquiryType.Question, text: "Question", iconText: "?" },
  { value: SupportInquiryType.Request, text: "Idea", icon: new URL('@webapp/assets/icons/sparkle_150.png', import.meta.url).href },
  { value: SupportInquiryType.Problem, text: "Problem", icon: ToolIcon },
]

export enum SupportTicketPriority {
    High = "High",
    Medium = "Medium",
    Low = "Low",
}

export enum SupportTicketStatus {
    Open = "Open",
    OnHold = "OnHold",
    Escalated = "Escalated",
    Closed = "Closed",
}

export enum UserGender {
    Male = "Male",
    Female = "Female",
}

export enum PrivacyLevel {
    Everyone = "Everyone",
    Groups = "Groups",
    Friends = "Friends",
    Me = "Me",
}

export enum DataGroupType {
    System = "System",
    Research = "Research",
    Profile = "Profile",
}

export enum GroupMemberRole {
    Viewer = "Viewer",
    Member = "Member",
    Contributor = "Contributor",
    Recruiter = "Recruiter",
    Administrator ="Administrator",
    Owner = "Owner",
}

export enum PrincipalType {
    User = "User",
    FamilyGroup = "FamilyGroup",
}

export enum GroupMemberVisibility {
    None = "None",
    Limited = "Limited",
    Full = "Full",
}

export enum ApprovalStatus {
    Pending = "Pending",
    Approved = "Approved",
    Rejected = "Rejected",
}

export enum DataGroupInviteStatus {
    Pending = "Pending",
    Approved = "Approved",
    Rejected = "Rejected",
    Expired = "Expired",
}

export const dataGroupInviteStatusText = {
    [DataGroupInviteStatus.Pending]: "Requested",
    [DataGroupInviteStatus.Approved]: "Approved",
    [DataGroupInviteStatus.Rejected]: "Declined",
    [DataGroupInviteStatus.Expired]: "Expired",
}

export const dataGroupInviteStatusStyle = {
    [DataGroupInviteStatus.Pending]: 'info',
    [DataGroupInviteStatus.Approved]: 'success',
    [DataGroupInviteStatus.Rejected]: undefined,
    [DataGroupInviteStatus.Expired]: undefined,
}

export enum InvitationResult {
    Pending = "Pending",
    Cancelled = "Cancelled",
    Accepted = "Accepted",
}

export enum ItemPermissions {
    None = 0,
    Read = 1 << 0,
    ReadContent = 1 << 1,
    AddContent = 1 << 2,
    ModifyContent = 1 << 3,
    ListItems = 1 << 4,
    ReadItems = 1 << 5,
    ModifyItems = 1 << 6,
    Modify = 1 << 7,
    AddItems = 1 << 8,
    DeleteItems = 1 << 9,
    Delete = 1 << 10,
    ReadPermissions = 1 << 11,
    ModifyPermissions = 1 << 12,    
    Owner = 1 << 13,
    ReadPrivate = 1 << 14,

    AllRead = Read | ReadContent | ListItems | ReadItems | ReadPrivate,
    AllEdit = AllRead | ModifyItems | ModifyContent | Modify | AddItems | DeleteItems,
    Administrator = AllEdit | ReadPermissions | ModifyPermissions,
}

export enum SubscriptionStatus {
    Active = "Active",
    Cancelled = "Cancelled",
    PastDue = "PastDue",
    Trial = "Trial",
    Incomplete = "Incomplete",
    IncompleteExpired = "IncompleteExpired",
}

export enum PaymentType {
    Card = "Card",
}

export enum PaymentStatus {
    Pending = "Pending",
    Succeeded = "Succeeded",
    Failed = "Failed",
}

export interface SubscriptionPlan {
    id: string
    baseType: string
    displayName: string
    description: string
    price?: number
    priceName: string,
    termMonths?: number
    trialDays?: number
    treeLimit?: number
    contentLimit: number
    shareLimit?: number
}

export const subscriptionPlans: SubscriptionPlan[] = [{ 
        id: 'mem.1',
        baseType: 'mem',
        displayName: 'Free', 
        description: 'For family members who want to view trees shared with them, and genealogists who want to share one tree with a few people',
        price: 0,
        priceName: 'Free',
        treeLimit: 1,
        contentLimit: 500,
        shareLimit: 5,
    }, {
        id: 'gen.1',
        baseType: 'gen',
        displayName: 'Basic',
        description: 'For genealogists who want to include more photos and documents and share with more people',
        price: 2.50,
        priceName: 'Practically free',
        termMonths: 6,
        treeLimit: 2,
        contentLimit: 5120, // 5 GB
        shareLimit: 15,
    }, {
        id: 'plus.1',
        baseType: 'plus',
        displayName: 'Everyone',
        description: 'For genealogists who want to share their trees with the whole family, with plenty of storage for high quality photos and documents.',
        price: 5,
        priceName: 'Totally reasonable',
        termMonths: 6,
        treeLimit: 5,
        contentLimit: 20480, // 20 GB
    }, {
        id: 'pro.1',
        baseType: 'pro',
        displayName: 'Professional',
        description: 'For professional genealogists who build family trees for others',
        price: 5,
        priceName: 'Pro deal',
        termMonths: 6,
        contentLimit: 0,
    }
]

export const defaultPlanId = "mem.1"

export function getPlanContentLimitText(limit: number) {
  if (limit < 1000) return `${limit} MB`
  return `${limit / 1000} GB`
}

export function getPlanTermPrice(plan: SubscriptionPlan) {
    return ((plan.price ?? 0) * (plan.termMonths ?? 12))
}
